import { useState, useEffect, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import { useTheme, keyframes } from "@mui/material/styles";
import {
  Typography,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ModeNightIcon from "@mui/icons-material/ModeNight";
import LightModeIcon from "@mui/icons-material/LightMode";

interface CriAppBarProps {
  themeMode: string;
  themeToggler: () => void;
}

interface CRIHeaderRoute {
  name: string;
  route: string;
  subRoutes?: CRIHeaderRoute[];
}

const HeaderRoutes: CRIHeaderRoute[] = [
  {
    name: "Home",
    route: "/",
  },
  // {
  //   name: "Beauty",
  //   route: "/beauty",
  // },
  {
    name: "Science",
    route: "/research",
    subRoutes: [
      {
        name: "Bees",
        route: "/research/bees",
      },
      {
        name: "Lichen",
        route: "/research/lichen",
      },
    ],
  },
  {
    name: "Place",
    route: "/education",
  },
  {
    name: "Essays and Links",
    route: "/essays",
  },
  {
    name: "Contact",
    route: "/contact",
  },
];

const slideDown = keyframes`
  from { transform: translateY(-100%); }
  to { transform: translateY(0); }
`;

const CriAppBar: React.FC<CriAppBarProps> = ({ themeMode, themeToggler }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [headerFixed, setHeaderFixed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Over a photo: white text on a soft dark gradient. Once scrolled: theme colors on paper.
  const headerStyle = headerFixed
    ? {
        position: "fixed" as const,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[4],
        animation: `${slideDown} 0.3s ease-out`,
      }
    : {
        position: "absolute" as const,
        background:
          "linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.25) 70%, transparent 100%)",
      };

  const textColor = headerFixed ? theme.palette.text.primary : "white";

  const navButtonStyle = (selected: boolean) => ({
    color: textColor,
    fontWeight: selected ? 600 : 500,
    fontSize: "15px",
    textTransform: "none" as const,
    textShadow: headerFixed ? "none" : "0 1px 3px rgba(0, 0, 0, 0.6)",
    display: {
      xs: "none",
      md: "block",
    },
    marginLeft: "8px",
    paddingX: "12px",
    borderRadius: 0,
    borderBottom: "2px solid",
    borderColor: selected ? theme.palette.primary.main : "transparent",
    transition: "border-color 0.2s ease, color 0.2s ease",
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: selected
        ? theme.palette.primary.main
        : headerFixed
        ? theme.palette.divider
        : "rgba(255, 255, 255, 0.6)",
    },
  });

  useEffect(() => {
    const handleScroll = () => setHeaderFixed(window.scrollY > 200);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isResearchPage = () => {
    return (
      location.pathname.length >= 9 &&
      location.pathname.substring(0, 9) === "/research"
    );
  };

  const handleMobileDrawerClick = (route: string) => {
    setIsDrawerOpen(false);
    navigate(route);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        top: 0,
        left: 0,
        paddingLeft: "24px",
        paddingRight: "24px",
        zIndex: theme.zIndex.drawer + 1,
        height: "74px",
        ...headerStyle,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          maxWidth: "1000px",
          minWidth: "100px",
          width: "100%",
        }}
      >
        <Typography
          component={Link}
          to="/"
          sx={{
            color: textColor,
            textDecoration: "none",
            fontWeight: 600,
            fontSize: { xs: "18px", sm: "22px" },
            textShadow: headerFixed ? "none" : "0 1px 3px rgba(0, 0, 0, 0.6)",
          }}
        >
          Conservation Research Institute
        </Typography>
        <Box sx={{ flexGrow: 1 }} />

        {
          // Render the header routes
          HeaderRoutes.map((route) => {
            return (
              <Button
                key={route.name}
                sx={navButtonStyle(
                  route.subRoutes
                    ? isResearchPage()
                    : location.pathname === route.route
                )}
                onClick={(event) => {
                  if (route.subRoutes) {
                    setAnchorEl(event.currentTarget);
                  } else {
                    navigate(route.route);
                  }
                }}
              >
                {route.name}
              </Button>
            );
          })
        }

        <IconButton
          onClick={themeToggler}
          sx={{
            color: textColor,
            display: {
              xs: "none",
              md: "flex",
            },
            marginLeft: {
              xs: 0,
              md: "12px",
            },
          }}
        >
          {themeMode === "dark" ? <LightModeIcon /> : <ModeNightIcon />}
        </IconButton>

        <Button
          variant="outlined"
          startIcon={<MenuIcon />}
          onClick={() => {
            setIsDrawerOpen(!isDrawerOpen);
          }}
          sx={{
            display: {
              xs: "flex",
              md: "none",
            },
            ...(!headerFixed && {
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.7)",
            }),
          }}
        >
          Menu
        </Button>
      </Box>

      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleCloseMenu} component={Link} to="/research">
          Research
        </MenuItem>
        <MenuItem
          onClick={handleCloseMenu}
          component={Link}
          to="/research/bees"
        >
          Bees
        </MenuItem>
        <MenuItem
          onClick={handleCloseMenu}
          component={Link}
          to="/research/lichen"
        >
          Lichen
        </MenuItem>
      </Menu>

      <Drawer
        anchor="top"
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
        }}
        sx={{
          "& .MuiDrawer-paper": {
            zIndex: theme.zIndex.drawer,
            top: "auto",
            marginTop: "74px",
          },
        }}
      >
        <List disablePadding>
          {
            // Render the header routes
            HeaderRoutes.map((route) => {
              return (
                <Fragment key={route.name}>
                  <ListItem
                    disableGutters
                    sx={{
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                  >
                    <ListItemButton
                      onClick={() => {
                        handleMobileDrawerClick(route.route);
                      }}
                    >
                      <ListItemText primary={route.name} />
                    </ListItemButton>
                  </ListItem>
                  {route.subRoutes &&
                    route.subRoutes.map((subRoute) => {
                      return (
                        <ListItem
                          disableGutters
                          sx={{
                            paddingTop: 0,
                            paddingBottom: 0,
                          }}
                          key={subRoute.name}
                        >
                          <ListItemButton
                            onClick={() => {
                              handleMobileDrawerClick(subRoute.route);
                            }}
                            sx={{
                              paddingLeft: "45px",
                            }}
                          >
                            <ListItemText primary={subRoute.name} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                </Fragment>
              );
            })
          }
        </List>
      </Drawer>
    </Box>
  );
};

export default CriAppBar;
